import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePageVariant } from "../../contexts/PageVariantContext";
import {
  HomeIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  CodeBracketIcon,
  FolderOpenIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";

const SECTIONS = [
  { id: "home", icon: HomeIcon, labelIt: "header.home" as const, labelRnd: "rndPageNav.home" as const },
  { id: "services", icon: BriefcaseIcon, labelIt: "header.services" as const, labelRnd: "rndPageNav.services" as const },
  { id: "about", icon: BuildingOffice2Icon, labelIt: "header.about" as const, labelRnd: "rndPageNav.about" as const },
  { id: "development", icon: CodeBracketIcon, labelIt: "header.development" as const, labelRnd: "rndPageNav.development" as const },
  { id: "portfolio", icon: FolderOpenIcon, labelIt: "header.portfolio" as const, labelRnd: "rndPageNav.portfolio" as const },
  {
    id: "testimonials",
    icon: ChatBubbleLeftRightIcon,
    labelIt: "itPageNav.testimonials" as const,
    labelRnd: "rndPageNav.testimonials" as const,
  },
  { id: "team", icon: UserGroupIcon, labelIt: "header.team" as const, labelRnd: "rndPageNav.team" as const },
  { id: "contact", icon: EnvelopeIcon, labelIt: "header.contact" as const, labelRnd: "rndPageNav.contact" as const },
] as const;

const ItPageNav = () => {
  const { t } = useTranslation();
  const variant = usePageVariant();
  const isRnd = variant === "rnd";
  const [active, setActive] = useState<string>("home");

  useEffect(() => {
    const updateActive = () => {
      const mid = window.scrollY + window.innerHeight / 2;
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const { id } = SECTIONS[i];
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.offsetTop;
        const bottom = top + el.offsetHeight;
        if (mid >= top && mid <= bottom) {
          setActive(id);
          return;
        }
      }
    };

    window.addEventListener("scroll", updateActive, { passive: true });
    updateActive();
    return () => window.removeEventListener("scroll", updateActive);
  }, []);

  const goTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const navShell = isRnd
    ? "border-purple-200/80 bg-white/95 shadow-purple-100/40"
    : "border-blue-200/80 bg-white/95 shadow-blue-100/40";

  const activeBtn = isRnd ? "bg-purple-600 text-white shadow-sm" : "bg-blue-600 text-white shadow-sm";

  const idleBtn = isRnd
    ? "text-zinc-500 hover:bg-purple-50 hover:text-purple-900"
    : "text-zinc-500 hover:bg-blue-50 hover:text-blue-900";

  return (
    <nav
      className={`it-page-nav-scroll fixed left-4 top-1/2 z-40 hidden max-h-[85vh] -translate-y-1/2 flex-col gap-1 overflow-y-auto rounded-xl border p-1.5 shadow-sm backdrop-blur-sm md:flex ${navShell}`}
      aria-label={t(isRnd ? "itPageNav.ariaLabelRnd" : "itPageNav.ariaLabel")}
    >
      {SECTIONS.map(({ id, icon: Icon, labelIt, labelRnd }) => (
        <button
          key={id}
          type="button"
          onClick={() => goTo(id)}
          className={`flex flex-col items-center gap-0.5 rounded-lg px-2.5 py-2 text-[10px] font-medium transition-colors ${
            active === id ? activeBtn : idleBtn
          }`}
        >
          <Icon className="h-4 w-4 shrink-0" aria-hidden />
          <span className="max-w-[4.5rem] text-center leading-tight">{t(isRnd ? labelRnd : labelIt)}</span>
        </button>
      ))}
    </nav>
  );
};

export default ItPageNav;
